import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';
import { PANEL_BASE } from '../config/paths';
import { useAuth } from '../context/AuthContext';
import { AppError } from '../api/request';
import AuthLayout from './AuthLayout';
import BrandLogo from './BrandLogo';

export default function Login() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? PANEL_BASE;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof AppError ? err.userMessage : t('auth.loginError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout>
      <div className="flex flex-col items-center text-center mb-8">
        <BrandLogo className="h-14 w-14 rounded-xl mb-4" />
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
          {t('auth.signInTo', { appName: t('common.appName') })}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="mt-6 space-y-4">
        {error && (
          <div className="rounded-md bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}
        <div>
          <label htmlFor="login-email" className="sr-only">
            {t('common.email')}
          </label>
          <input
            id="login-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            className="input rounded-lg"
            placeholder={t('auth.emailPlaceholder')}
            required
          />
        </div>
        <div>
          <label htmlFor="login-password" className="sr-only">
            {t('common.password')}
          </label>
          <div className="relative">
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="input rounded-lg pr-10"
              placeholder={t('common.password')}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(v => !v)}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              aria-label={showPassword ? t('common.hidePassword', { defaultValue: 'Hide password' }) : t('common.showPassword', { defaultValue: 'Show password' })}
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="btn btn-primary w-full py-2.5 font-medium rounded-lg"
        >
          {submitting ? t('auth.loggingIn') : t('auth.login')}
        </button>
        <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600 dark:text-gray-400">
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={e => setRememberMe(e.target.checked)}
            className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
          />
          {t('auth.rememberMe')}
        </label>
      </form>

      <div className="mt-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
        <span className="text-gray-400 dark:text-gray-500 cursor-default" title={t('auth.forgotPasswordComingSoon')}>
          {t('auth.forgotPassword')}
        </span>
        <Link to="/register" className="hover:text-primary-600 dark:hover:text-primary-400 hover:underline font-medium">
          {t('auth.signUpLink')}
        </Link>
        <Link to="/" className="hover:text-primary-600 dark:hover:text-primary-400 hover:underline">
          {t('auth.customerDashboard')}
        </Link>
      </div>
    </AuthLayout>
  );
}
